/**
 * Body of the "User Details" modal on the User Activity page: summary stat
 * tiles, top-pages / top-events pill rows, and a recent-activity table.
 */
import { formatNumber, summarizeEventData } from '../lib/utils';
import { formatDateTime } from '@/lib/utils/dates';
import { Stat } from './parts';
import type { ActivityUserDetail } from '../types';

/** Accent-tinted pill used in the top-pages / top-events rows. */
function Pill({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-center rounded-full bg-[var(--accent-light)] px-2.5 py-0.5 text-xs text-[color:var(--accent)]">
      {children}
    </span>
  );
}

/** A labelled pill row, with an empty-state message when there are no items. */
function PillSection({
  title,
  emptyLabel,
  children,
  isEmpty,
}: {
  title: string;
  emptyLabel: string;
  children: React.ReactNode;
  isEmpty: boolean;
}): JSX.Element {
  return (
    <div>
      <h4 className="mb-2 text-sm font-semibold text-[color:var(--text-dark)]">{title}</h4>
      <div className="flex flex-wrap gap-1.5">
        {isEmpty ? (
          <span className="text-sm text-[color:var(--text-dark)]">{emptyLabel}</span>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

const TH_CLASS =
  'border-b border-[color:var(--gray-200)] px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--text-dark)]';

export default function ActivityUserDetailModalBody({
  detail,
}: {
  detail: ActivityUserDetail;
}): JSX.Element {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Total Events" value={formatNumber(detail.total_events)} />
        <Stat label="Page Views" value={formatNumber(detail.page_views)} />
        <Stat label="Docs Viewed" value={formatNumber(detail.doc_views)} />
        <Stat label="First Active" value={formatDateTime(detail.first_active)} small />
        <Stat label="Last Active" value={formatDateTime(detail.last_active)} small />
      </div>

      <PillSection
        title="Top Pages"
        emptyLabel="No pages recorded"
        isEmpty={detail.top_pages.length === 0}
      >
        {detail.top_pages.map((p) => (
          <Pill key={p.page_path}>
            {p.page_path} ({p.views})
          </Pill>
        ))}
      </PillSection>

      <PillSection
        title="Top Events"
        emptyLabel="No events recorded"
        isEmpty={detail.top_events.length === 0}
      >
        {detail.top_events.map((e) => (
          <Pill key={e.event_name}>
            {e.event_name} ({e.count})
          </Pill>
        ))}
      </PillSection>

      <div>
        <h4 className="mb-2 text-sm font-semibold text-[color:var(--text-dark)]">
          Recent Activity
        </h4>
        <div className="overflow-x-auto rounded-md border border-[color:var(--gray-200)]">
          <table className="min-w-full border-collapse text-sm font-[var(--font-body)]">
            <thead>
              <tr className="bg-[var(--light-bg)]">
                <th className={TH_CLASS}>Event</th>
                <th className={TH_CLASS}>Description</th>
                <th className={TH_CLASS}>Time</th>
              </tr>
            </thead>
            <tbody>
              {detail.recent_activity.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-3 py-4 text-center text-sm text-[color:var(--text-dark)]"
                  >
                    No recent activity
                  </td>
                </tr>
              ) : (
                detail.recent_activity.map((a, idx) => (
                  <tr
                    key={`${a.event_name}-${a.created_at}-${idx}`}
                    className="border-b border-[color:var(--gray-200)] last:border-b-0"
                  >
                    <td className="px-3 py-2 text-[color:var(--text-dark)]">{a.event_name}</td>
                    <td className="px-3 py-2 text-[color:var(--text-dark)]">
                      {a.description || summarizeEventData(a.event_data)}
                    </td>
                    <td className="px-3 py-2 text-[color:var(--text-dark)]">
                      {formatDateTime(a.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
